import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import { getFileTypeFromExtension } from '../utils/fileExtensionManager';
import getAuthorizedFilePreviewUrl from '../services/getAuthorizedFilePreviewUrl';
import getWacInfoForSharePointFile from '../services/getWacInfoForSharePointFile';
import { getContentOfSharePointTextFile } from '../services/getContentOfSharePointTextFile';
import {
    FileExtensionTypes,
    SharePointFileInformation,
    getFilesPreviewInformationStore,
    updateFilePreviewInformation,
    PREFETCH_VALID_TIME_IN_MILLISECOND,
} from 'owa-group-files-hub-store';
import { getCurrentGroupSmtpAddress } from 'owa-groups-shared-store';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import { logServerCallErrorMessage } from 'owa-server-call-logging/lib/owaServerCallLogging';
import { logGeneralErrorMessage } from '../logging/genericInformationLogging';

export default function prefetchSharePointFilePreviewInformation(file: SharePointFileInformation) {
    const previewInformationStore = getFilesPreviewInformationStore();
    if (
        previewInformationStore.previewInformation &&
        previewInformationStore.previewInformation[file.id] &&
        previewInformationStore.previewInformation[file.id].lastUpdateTime &&
        new Date().getTime() -
            previewInformationStore.previewInformation[file.id].lastUpdateTime.getTime() <
            PREFETCH_VALID_TIME_IN_MILLISECOND
    ) {
        // we already have the preview information which is still valid.
        return;
    }
    const datapoint = new PerformanceDatapoint(
        'GroupFilesHubPrefetchFilePreviewInformationActionDP'
    );
    datapoint.addCustomProperty('fileId', file.id);
    const fileType = getFileTypeFromExtension(file.extension);
    switch (fileType) {
        case FileExtensionTypes.Audio:
        case FileExtensionTypes.Pdf:
        case FileExtensionTypes.Video:
            handleIframePreviewFilesPrefetch(file, datapoint);
            break;
        case FileExtensionTypes.Text:
            handleTextPrefetch(file, datapoint);
            break;
        case FileExtensionTypes.Wac:
            handleWacPrefetch(file, datapoint);
            break;
        case FileExtensionTypes.Others:
            handleUnsupportedFileTypes(file, datapoint);
            break;
    }
}

function handleIframePreviewFilesPrefetch(
    file: SharePointFileInformation,
    datapoint: PerformanceDatapoint
) {
    getAuthorizedFilePreviewUrl(file.url, 'Prefetch')
        .then((previewUrl: string) => {
            if (getCurrentGroupSmtpAddress() != file.groupSmtpAddress) {
                datapoint.end();
            } else if (previewUrl) {
                updateFilePreviewInformation(file.id, {
                    previewUrl: previewUrl,
                    lastUpdateTime: new Date(),
                });
                datapoint.end();
            } else {
                logGeneralErrorMessage(datapoint, 'PreviewUrl is invalid.');
                datapoint.endWithError(DatapointStatus.ServerError);
            }
        })
        .catch(error => {
            logServerCallErrorMessage(datapoint, error.toString());
            datapoint.endWithError(DatapointStatus.ServerError);
        });
}

function handleTextPrefetch(file: SharePointFileInformation, datapoint: PerformanceDatapoint) {
    getContentOfSharePointTextFile(file.id, file.url, 'Prefetch')
        .then((textContent: string) => {
            if (getCurrentGroupSmtpAddress() != file.groupSmtpAddress) {
                datapoint.end();
            } else if (textContent) {
                updateFilePreviewInformation(file.id, {
                    Content: textContent,
                    lastUpdateTime: new Date(),
                });
                datapoint.end();
            } else {
                logGeneralErrorMessage(datapoint, 'TextContent is invalid.');
                datapoint.endWithError(DatapointStatus.ServerError);
            }
        })
        .catch(error => {
            logServerCallErrorMessage(datapoint, error.toString());
            datapoint.endWithError(DatapointStatus.ServerError);
        });
}

function handleWacPrefetch(file: SharePointFileInformation, datapoint: PerformanceDatapoint) {
    getWacInfoForSharePointFile(file.url, AttachmentDataProviderType.OneDrivePro, 'Prefetch')
        .then((previewUrl: string) => {
            if (getCurrentGroupSmtpAddress() != file.groupSmtpAddress) {
                datapoint.end();
            } else if (previewUrl) {
                updateFilePreviewInformation(file.id, {
                    previewUrl: previewUrl,
                    lastUpdateTime: new Date(),
                });
                datapoint.end();
            } else {
                logGeneralErrorMessage(datapoint, 'PreviewUrl is invalid.');
                datapoint.endWithError(DatapointStatus.ServerError);
            }
        })
        .catch(error => {
            logServerCallErrorMessage(datapoint, error.toString());
            datapoint.endWithError(DatapointStatus.ServerError);
        });
}

function handleUnsupportedFileTypes(
    file: SharePointFileInformation,
    datapoint: PerformanceDatapoint
) {
    logGeneralErrorMessage(datapoint, 'Unsupported file extension.' + file.extension);
    datapoint.endWithError(DatapointStatus.ClientError);
}
